import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { clearPosts, fetchInstagramMedia } from "../../Store/instagramSlice";
import { callNonStoreApi, callNonStoreApiNew } from "../../NonStoreApis";
import { FetchInstaCommentToDmPosts, DeleteAutomation } from "../../Api/ApiEndPoints";
import { AddFlex, CustomText } from "../../reusableStyles";
import useWindowSize from "../../Hooks/useWindowSize";
import { Button, Skeleton } from "antd";
import { enqueueSnackbar } from "notistack";
import { primaryActionColor, THUNK_API_STATES } from "../../constants";
import { CircularProgress, Drawer } from "@mui/material";
import useIntegrationHooks from "../../CustomHooks/useIntegrationHooks";
import WebAutomationCreation from "./WebAutomationCreation";
import { useInView } from "react-intersection-observer";

// ═══════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 280px);
  gap: 8px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
  }
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  opacity: 0;
  transition: opacity 0.18s;
  display: flex;
  align-items: flex-end;
  padding: 8px;
`;

const OverlayLabel = styled.span`
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
`;

const GridItem = styled.div`
  display: flex;
  width: 280px;
  height: 140px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: white;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.18s, transform 0.18s;

  @media (max-width: 1000px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    display: block;
    border: none;
    box-shadow: none;
    border-radius: 6px;
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:hover ${GridOverlay} {
    opacity: 1;
  }
`;

const ThumbBox = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
  background: #f3f4f6;
  overflow: hidden;

  @media (max-width: 1000px) {
    width: 100%;
    height: 100%;
  }
`;

const GridImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AutomationDot = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.isPaused ? "#f59e0b" : "#22c55e")};
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  z-index: 2;
`;

const MediaTypeIcon = styled.div`
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 2;
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  gap: 2px;
`;

// ── Stats panel (right side of each card, desktop only) ──────────────

const StatsPanel = styled.div`
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  background: white;

  @media (max-width: 1000px) {
    display: none;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) =>
    p.live ? "#dcfce7" : p.paused ? "#fef3c7" : "#f1f5f9"};
  color: ${(p) =>
    p.live ? "#166534" : p.paused ? "#92400e" : "#64748b"};
`;

const StatsDuo = styled.div`
  display: flex;
  gap: 10px;
`;

const StatBlock = styled.div`
  flex: 1;
`;

const StatNum = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
`;

const StatLbl = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 1px;
`;

// ── Misc ─────────────────────────────────────────────────────────────

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
  max-width: 900px;
`;

const Tab = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(p) => (p.active ? "600" : "400")};
  color: ${(p) => (p.active ? primaryActionColor : "#6b7280")};
  border-bottom: 2px solid ${(p) => (p.active ? primaryActionColor : "transparent")};
  transition: all 0.2s;
  &:hover { color: ${primaryActionColor}; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  max-width: 900px;
  width: 100%;
`;

const NextPostCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px dashed #d1d5db;
  background: #fafafa;
  cursor: pointer;
  max-width: 422px;
  margin-bottom: 16px;
  transition: border-color 0.2s, background 0.2s;
  &:hover { border-color: #9ca3af; background: #f3f4f6; }
`;

const NextPostIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════

const fmt = (n) => {
  if (!n) return "0";
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return n.toLocaleString();
};

const getMediaTypeLabel = (mediaType, isStory) => {
  if (isStory) return "Story";
  if (mediaType === "VIDEO" || mediaType === "REELS") return "Reel";
  if (mediaType === "CAROUSEL_ALBUM") return "Carousel";
  return null;
};

const deliveryRatio = (sent, comments) => {
  if (!comments || !sent) return null;
  return Math.round((sent / comments) * 100);
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

function InstaCommentToDmAutomation({ initialMediaId, initialAutomation, onPostSelected } = {}) {
  const dispatch = useDispatch();
  const { isMobileView } = useWindowSize();
  const { credentials, posts: igPosts, otherData } = useSelector((s) => s.instagram);

  const [isApiLoading, setIsApiLoading] = useState(true);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [automations, setAutomations] = useState({});
  const [instagramMedia, setInstagramMedia] = useState([]);

  const [showCreation, setShowCreation] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [bottomSheet, setBottomSheet] = useState(null);

  useEffect(() => {
    if (initialAutomation) {
      const md = initialAutomation.mediaData || {};
      const media = md.isNextPost
        ? { isNextPost: true }
        : { id: md.mediaId || initialMediaId, mediaId: md.mediaId || initialMediaId, mediaUrl: md.mediaUrl, thumbnailUrl: md.mediaUrl, mediaType: md.media_type, media_type: md.media_type, caption: md.caption || "", isStory: md.isStory || false };
      if (onPostSelected) { onPostSelected(media, initialAutomation, true); return; }
      setSelectedMedia(media);
      setSelectedAutomation(initialAutomation);
      setIsEditMode(true);
      setShowCreation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isIntegrationActivated } = useIntegrationHooks("instagram_connect", () => {}, () => {});
  const { ref: lastElementRef, inView } = useInView({ threshold: 0.5 });

  const fetchAutomations = useCallback(async () => {
    if (!credentials?.user_id) return;
    try {
      const res = await callNonStoreApi(FetchInstaCommentToDmPosts, { instaUserId: credentials.user_id });
      if (res.status === 200) setAutomations(res.data || {});
    } catch (e) {
      console.error("Error fetching automations:", e);
    } finally {
      setIsApiLoading(false);
    }
  }, [credentials]);

  const fetchMedia = useCallback(async () => {
    if (!credentials?.user_id || !credentials?.access_token) return;
    setIsMediaLoading(true);
    dispatch(clearPosts());
    dispatch(fetchInstagramMedia({ userId: credentials.user_id, accessToken: credentials.access_token }))
      .then((r) => { if (r.meta.requestStatus === THUNK_API_STATES.fulfilled) setIsMediaLoading(false); });
  }, [credentials, dispatch]);

  useEffect(() => {
    if (inView && otherData?.nextPostsSet && tabIndex === 0)
      dispatch(fetchInstagramMedia({ paginatedUrl: otherData.nextPostsSet }));
  }, [inView, otherData?.nextPostsSet, tabIndex, dispatch]);

  useEffect(() => {
    setInstagramMedia(igPosts.map((p) => ({
      id: p.id, mediaId: p.id,
      mediaUrl: p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url,
      thumbnailUrl: p.thumbnail_url || p.media_url,
      mediaType: p.media_type, media_type: p.media_type,
      caption: p.caption || "", isStory: false,
      commentCount: p.comments_count ?? null,
    })));
  }, [igPosts]);

  useEffect(() => { fetchAutomations(); fetchMedia(); }, [credentials]);

  useEffect(() => {
    const handleRefresh = () => { fetchAutomations(); fetchMedia(); };
    window.addEventListener("refresh-automations", handleRefresh);
    return () => window.removeEventListener("refresh-automations", handleRefresh);
  }, [fetchAutomations, fetchMedia]);

  // ─── Computed ─────────────────────────────────────────────────────
  const getAuto = (mediaId) => automations[mediaId] || null;
  const nextPostAuto = automations["__next_post__"] || null;

  const automatedMedia = Object.entries(automations)
    .filter(([k]) => k !== "__next_post__")
    .map(([, auto]) => {
      const matched = instagramMedia.find((m) => m.id === auto.mediaData?.mediaId);
      return {
        id: auto.mediaData?.mediaId, mediaId: auto.mediaData?.mediaId,
        mediaUrl: auto.mediaData?.mediaUrl, thumbnailUrl: auto.mediaData?.mediaUrl,
        mediaType: auto.mediaData?.media_type, media_type: auto.mediaData?.media_type,
        caption: "", isStory: auto.mediaData?.isStory || false,
        commentCount: matched?.commentCount ?? null,
        automation: auto,
      };
    });

  const displayMedia = tabIndex === 0 ? instagramMedia : automatedMedia;

  // ─── Handlers ─────────────────────────────────────────────────────
  const openCreationFor = (media, auto) => {
    if (onPostSelected) { onPostSelected(media, auto || null, !!auto); return; }
    setSelectedMedia(media);
    setSelectedAutomation(auto || null);
    setIsEditMode(!!auto);
    setShowCreation(true);
  };

  const handleMediaClick = (media) => {
    const auto = tabIndex === 0 ? getAuto(media.id) : media.automation;
    if (isMobileView() && auto) { setBottomSheet({ media, auto }); return; }
    openCreationFor(media, auto);
  };

  const handleNextPostClick = () => {
    if (!isIntegrationActivated()) { enqueueSnackbar("Please integrate an instagram account", { variant: "error" }); return; }
    openCreationFor({ isNextPost: true }, nextPostAuto || null);
  };

  const handleDeleteNextPost = async () => {
    if (!nextPostAuto?.id) return;
    try {
      const res = await callNonStoreApiNew("delete", DeleteAutomation.replace(":automationId", encodeURIComponent(nextPostAuto.id)));
      if (res.status === 200 && res.data?.success) { enqueueSnackbar("Next Post automation deleted", { variant: "success" }); await fetchAutomations(); }
      else enqueueSnackbar("Couldn't delete automation", { variant: "error" });
    } catch { enqueueSnackbar("Couldn't delete automation", { variant: "error" }); }
  };

  const handleCloseCreation = () => { setShowCreation(false); setSelectedMedia(null); setSelectedAutomation(null); setIsEditMode(false); };
  const handleSaveComplete = async () => { await fetchAutomations(); dispatch(clearPosts()); fetchMedia(); };

  if (showCreation) {
    return (
      <WebAutomationCreation
        media={selectedMedia} automation={selectedAutomation} isEdit={isEditMode}
        onClose={handleCloseCreation} onSave={handleSaveComplete}
        instaUserId={credentials?.user_id} instaId={credentials?.id} credentials={credentials}
      />
    );
  }

  const isLoading = isApiLoading || isMediaLoading;

  // ─── Sub-renders ──────────────────────────────────────────────────

  const renderStatsPanel = (auto, media) => {
    const isPaused = auto?.status === "paused";
    const stats = auto?._stats;

    if (!auto) {
      return (
        <StatsPanel>
          <div>
            <StatusPill style={{ background: "#f1f5f9", color: "#94a3b8" }}>Not automated</StatusPill>
          </div>
          {media?.commentCount != null && (
            <StatsDuo>
              <StatBlock>
                <StatNum>{fmt(media.commentCount)}</StatNum>
                <StatLbl>comments</StatLbl>
              </StatBlock>
            </StatsDuo>
          )}
          <div style={{ fontSize: 11, color: primaryActionColor, fontWeight: 600 }}>
            Click to automate →
          </div>
        </StatsPanel>
      );
    }

    const ratio = deliveryRatio(stats?.dmsSent, media?.commentCount);
    const ratioColor = ratio == null ? null
      : ratio === 100 ? "#16a34a"
      : ratio >= 80   ? "#d97706"
      :                  "#dc2626";

    return (
      <StatsPanel>
        <StatusPill live={!isPaused} paused={isPaused}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: isPaused ? "#d97706" : "#16a34a", display: "inline-block" }} />
          {isPaused ? "Paused" : "Live"}
        </StatusPill>
        <StatsDuo>
          {media?.commentCount != null && (
            <StatBlock>
              <StatNum>{fmt(media.commentCount)}</StatNum>
              <StatLbl>comments</StatLbl>
            </StatBlock>
          )}
          {stats && (
            <StatBlock>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <StatNum>{fmt(stats.dmsSent)}</StatNum>
                {ratioColor && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: ratioColor }}>
                    {ratio}%
                  </span>
                )}
              </div>
              <StatLbl>DMs sent</StatLbl>
            </StatBlock>
          )}
        </StatsDuo>
      </StatsPanel>
    );
  };

  return (
    <>
      <AddFlex flexDirection="column" padding="0">
        {/* Tabs */}
        <AddFlex alignItems="center" margin="10px 0" wrap="wrap">
          <TabsContainer>
            <Tab active={tabIndex === 0} onClick={() => setTabIndex(0)}>Feed</Tab>
            <Tab active={tabIndex === 1} onClick={() => setTabIndex(1)}>
              With Automations
              {Object.keys(automations).filter(k => k !== "__next_post__").length > 0 && (
                <span style={{ marginLeft: 6, background: primaryActionColor, color: "white", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>
                  {Object.keys(automations).filter(k => k !== "__next_post__").length}
                </span>
              )}
            </Tab>
          </TabsContainer>
        </AddFlex>

        {tabIndex === 0 && !isLoading && (
          <CustomText fontSize="12px" color="#6b7280" margin="0 0 12px 0">
            Click on any post to automate comments for it
          </CustomText>
        )}


        {/* Grid */}
        {isLoading && tabIndex === 0 ? (
          <GridContainer>
            {[...Array(6)].map((_, i) => (
              <GridItem key={i} style={{ cursor: "default" }}>
                <ThumbBox><Skeleton.Node active style={{ width: "100%", height: "100%" }} /></ThumbBox>
                <StatsPanel>
                  <Skeleton.Node active style={{ width: "80%", height: 16, borderRadius: 8 }} />
                  <Skeleton.Node active style={{ width: "60%", height: 24, borderRadius: 8 }} />
                  <Skeleton.Node active style={{ width: "40%", height: 12, borderRadius: 8 }} />
                </StatsPanel>
              </GridItem>
            ))}
          </GridContainer>
        ) : isApiLoading && tabIndex === 1 ? (
          <AddFlex flexDirection="column" padding="48px" alignItems="center" gap="10px" justify="center">
            <CircularProgress />
            <CustomText>Loading automations...</CustomText>
          </AddFlex>
        ) : displayMedia.length === 0 ? (
          <EmptyState>
            {tabIndex === 1 ? (
              <>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 28 }}>⚡</div>
                <CustomText color="#1f2937" fontSize="16px" fontWeight="600" margin="0 0 8px 0">No automations yet</CustomText>
                <CustomText color="#6b7280" fontSize="14px" margin="0 0 20px 0">Select a post from "Feed" tab to create your first automation</CustomText>
                <Button type="primary" onClick={() => setTabIndex(0)} style={{ backgroundColor: primaryActionColor }}>Browse Posts</Button>
              </>
            ) : (
              <CustomText color="#6b7280" fontSize="14px">No posts found. Make sure your Instagram account is connected.</CustomText>
            )}
          </EmptyState>
        ) : (
          <GridContainer>

            {displayMedia.map((media, index) => {
              const auto = tabIndex === 0 ? getAuto(media.id) : media.automation;
              const isPaused = auto?.status === "paused";
              const mediaTypeLabel = getMediaTypeLabel(media.mediaType || media.media_type, media.isStory);
              const isLast = index === displayMedia.length - 1;

              return (
                <GridItem
                  key={media.id || index}
                  ref={isLast && tabIndex === 0 ? lastElementRef : null}
                  onClick={() => handleMediaClick(media)}
                >
                  <ThumbBox>
                    <GridImage
                      src={media.thumbnailUrl || media.mediaUrl}
                      alt=""
                      loading="lazy"
                      onError={(e) => { e.target.style.background = "#e5e7eb"; }}
                    />
                    {auto && <AutomationDot isPaused={isPaused} />}
                    {mediaTypeLabel && (
                      <MediaTypeIcon>
                        {mediaTypeLabel === "Reel" && "▶"}
                        {mediaTypeLabel === "Carousel" && "❐"}
                        {mediaTypeLabel === "Story" && "◎"} {mediaTypeLabel}
                      </MediaTypeIcon>
                    )}
                    <GridOverlay className="grid-overlay">
                      <OverlayLabel>{auto ? (isPaused ? "Paused · Edit" : "Live · Edit") : "Tap to automate"}</OverlayLabel>
                    </GridOverlay>
                  </ThumbBox>

                  {renderStatsPanel(auto, media)}
                </GridItem>
              );
            })}
          </GridContainer>
        )}
      </AddFlex>

      {/* ── Mobile bottom sheet ─────────────────────────────────────── */}
      <Drawer
        anchor="bottom"
        open={!!bottomSheet}
        onClose={() => setBottomSheet(null)}
        PaperProps={{ sx: { borderRadius: "20px 20px 0 0", maxHeight: "70vh" } }}
      >
        {bottomSheet && (() => {
          const { media, auto } = bottomSheet;
          const isPaused = auto?.status === "paused";
          const stats = auto?._stats;
          const thumb = media.thumbnailUrl || media.mediaUrl;
          return (
            <div style={{ padding: "0 20px 32px" }}>
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 20px" }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: "#e2e8f0" }} />
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 20 }}>
                <div style={{ width: 64, height: 64, borderRadius: 10, overflow: "hidden", background: "#f3f4f6", flexShrink: 0 }}>
                  {thumb
                    ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>✨</div>
                  }
                </div>
                <div>
                  <StatusPill live={!isPaused} paused={isPaused} style={{ marginBottom: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: isPaused ? "#d97706" : "#16a34a", display: "inline-block" }} />
                    {isPaused ? "Paused" : "Live"}
                  </StatusPill>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {media.caption ? media.caption.slice(0, 60) + (media.caption.length > 60 ? "…" : "") : "No caption"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
                  {media.commentCount != null && (
                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 12, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{fmt(media.commentCount)}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, marginTop: 3 }}>comments</div>
                    </div>
                  )}
                  {stats && (
                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 12, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{fmt(stats.dmsSent)}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, marginTop: 3 }}>DMs sent</div>
                    </div>
                  )}
                  {deliveryRatio(stats?.dmsSent, media.commentCount) != null && (
                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 12, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{deliveryRatio(stats.dmsSent, media.commentCount)}%</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, marginTop: 3 }}>delivery</div>
                    </div>
                  )}
                </div>
              <button
                onClick={() => { setBottomSheet(null); openCreationFor(media, auto); }}
                style={{ width: "100%", padding: "14px 0", borderRadius: 12, border: "none", background: primaryActionColor, color: "white", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
              >
                Edit automation
              </button>
            </div>
          );
        })()}
      </Drawer>
    </>
  );
}

export default InstaCommentToDmAutomation;
